import {UWM_REFRESH_TOKEN} from '@uwm/host';
import log from '@uwm/logger';
import {UamfHttpError} from '@uwm/uamf';

import {TOKEN_TYPES} from '@/consts/tokenTypes';
import uwmRequestService from '@/services/uwm/uwmRequestService';
import {setUamToken} from '@/state/ducks/insights';
import {selectInsightsContext} from '@/state/selectors/insights';
import {dispatch, getState} from '@/state/store';

const EXPIRY_SKEW_MS = 30_000;

const isTokenExpiringSoon = (token: string): boolean => {
    if (!token) return false;

    try {
        const [, payload] = token.split('.');
        const {exp} = JSON.parse(atob(payload)) as {exp?: number};

        if (!exp) return false;

        return exp * 1000 - Date.now() < EXPIRY_SKEW_MS;
    } catch {
        return false;
    }
};

const isUnauthorized = (error: unknown) => error instanceof UamfHttpError && error.status === 401;

const refreshToken = async (): Promise<string> => {
    try {
        const response = await uwmRequestService.request(UWM_REFRESH_TOKEN, {tokenType: TOKEN_TYPES.UAM});
        const token = (response.token as string) ?? '';

        dispatch(setUamToken(token));

        return token;
    } catch (e) {
        log.error('uamfTokenService: token refresh failed', (e as Error).message);

        return '';
    }
};

const callWithToken = async <T>(perform: (token: string) => Promise<T>): Promise<T> => {
    let token = selectInsightsContext(getState())?.uamToken ?? '';

    if (isTokenExpiringSoon(token)) {
        token = (await refreshToken()) || token;
    }

    try {
        return await perform(token);
    } catch (e) {
        if (!isUnauthorized(e)) throw e;

        const freshToken = await refreshToken();

        return perform(freshToken || token);
    }
};

export default {isTokenExpiringSoon, refreshToken, callWithToken};
